/**
 * Info tab UI: renders workbook + per-sheet summary inside collapsible
 * <details> sections. Lazy — scan fires on first tab activation and on
 * explicit refresh.
 */
import { scanWorkbookInfo } from "../info/scanner";
import type { NamedRangeSummary, SheetInfo, WorkbookInfo } from "../info/types";

interface InfoUIDeps {
  log: (message: string, level?: "info" | "success" | "error") => void;
}

let initialized = false;
let panel: HTMLElement | null = null;
let refreshBtn: HTMLButtonElement | null = null;
let body: HTMLElement | null = null;
let lastScanAt: number | null = null;

export async function initInfoUI(deps: InfoUIDeps): Promise<void> {
  if (initialized) return;
  initialized = true;

  panel = document.querySelector<HTMLElement>('[data-tab-panel="info"]');
  if (!panel) {
    deps.log("info tab panel missing in HTML", "error");
    return;
  }

  refreshBtn = panel.querySelector<HTMLButtonElement>("#info-refresh");
  body = panel.querySelector<HTMLElement>("#info-body");

  if (refreshBtn) {
    refreshBtn.addEventListener("click", () => {
      void refresh(deps);
    });
  }

  // Also fire whenever the user clicks the Info tab — but only if we
  // haven't scanned recently. Avoids re-running on every tab switch.
  const tabBtn = document.querySelector<HTMLButtonElement>(
    '.tab[data-tab="info"]',
  );
  if (tabBtn) {
    tabBtn.addEventListener("click", () => {
      const stale = lastScanAt === null || Date.now() - lastScanAt > 60_000;
      if (stale) void refresh(deps);
    });
  }

  // First scan now if the Info tab is the active default at startup.
  if (panel && !panel.hidden) {
    void refresh(deps);
  }
}

async function refresh(deps: InfoUIDeps): Promise<void> {
  if (!body || !refreshBtn) return;
  refreshBtn.disabled = true;
  body.innerHTML = `<p class="info-loading">Scanning workbook…</p>`;
  try {
    const info = await scanWorkbookInfo();
    lastScanAt = Date.now();
    render(info);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    body.innerHTML = `<p class="info-error">Couldn't scan: ${escapeHtml(msg)}</p>`;
    deps.log(`info scan failed: ${msg}`, "error");
  } finally {
    refreshBtn.disabled = false;
  }
}

function render(info: WorkbookInfo): void {
  if (!body) return;

  const parts: string[] = [];

  // Workbook overview section
  parts.push(`
    <details class="info-section" open>
      <summary class="info-section-header">
        <span class="info-section-title">Workbook</span>
        <span class="info-section-subtitle">${escapeHtml(info.filename)}</span>
      </summary>
      <div class="info-section-body">
        <dl class="info-grid">
          <dt>Filename</dt><dd>${escapeHtml(info.filename)}</dd>
          <dt>Sheets</dt><dd>${info.sheetCount} (${info.visibleSheetCount} visible, ${info.hiddenSheetCount} hidden)</dd>
          <dt>Named ranges</dt><dd>${fmtNum(info.namedRangeCount)}</dd>
          <dt>Used cells (all sheets)</dt><dd>${fmtNum(info.totalUsedCells)}</dd>
          <dt>Formula cells (all sheets)</dt><dd>${fmtNum(info.totalFormulaCells)}</dd>
        </dl>
      </div>
    </details>
  `);

  // One section per sheet, in workbook order.
  for (const sheet of info.sheets) {
    parts.push(renderSheetSection(sheet));
  }

  // Named ranges section, below the worksheets list.
  parts.push(renderNamedRangesSection(info.namedRanges));

  body.innerHTML = parts.join("");
}

function renderSheetSection(s: SheetInfo): string {
  const visBadge =
    s.visibility === "visible"
      ? ""
      : `<span class="info-badge info-badge-${s.visibility}">${s.visibility}</span>`;
  const isEmpty = s.usedRange === null;
  const used = fmtNum(s.usedCellCount);
  const blank = fmtNum(s.blankCellCount);
  const total = fmtNum(s.rowCount * s.columnCount);
  const formulas = fmtNum(s.formulaCellCount);

  return `
    <details class="info-section">
      <summary class="info-section-header">
        <span class="info-section-title">${escapeHtml(s.name)}</span>
        ${visBadge}
        ${isEmpty
          ? `<span class="info-section-subtitle">empty</span>`
          : `<span class="info-section-subtitle">${escapeHtml(s.usedRange ?? "")}</span>`}
      </summary>
      <div class="info-section-body">
        ${isEmpty ? `<p class="info-empty">This sheet has no used range.</p>` : `
          <dl class="info-grid">
            <dt>Used range</dt><dd>${escapeHtml(s.usedRange ?? "")}</dd>
            <dt>Dimensions</dt><dd>${fmtNum(s.rowCount)} × ${fmtNum(s.columnCount)} = ${total} cells</dd>
            <dt>Non-empty</dt><dd>${used}</dd>
            <dt>Blank (inside used range)</dt><dd>${blank}</dd>
            <dt>Formulas</dt><dd>${formulas}</dd>
            <dt>Visibility</dt><dd>${escapeHtml(s.visibility)}</dd>
          </dl>
        `}
      </div>
    </details>
  `;
}

function renderNamedRangesSection(names: NamedRangeSummary[]): string {
  const count = names.length;
  const invalidCount = names.filter((n) => !n.isValid).length;
  const invalidBadge = invalidCount > 0
    ? `<span class="info-badge info-badge-veryHidden">${invalidCount} broken</span>`
    : "";
  if (count === 0) {
    return `
      <details class="info-section">
        <summary class="info-section-header">
          <span class="info-section-title">Named ranges</span>
          <span class="info-section-subtitle">none</span>
        </summary>
        <div class="info-section-body">
          <p class="info-empty">This workbook has no named ranges.</p>
        </div>
      </details>
    `;
  }
  // Show as a tight table inside the body. For deeper inspection (search,
  // sort, navigate), the "List named ranges" action opens the full dialog.
  const rows: string[] = [];
  for (const n of names) {
    const invalid = n.isValid ? "" : ' class="nr-row-invalid"';
    rows.push(
      `<tr${invalid}>` +
        `<td class="info-nr-name">${escapeHtml(n.name)}</td>` +
        `<td class="info-nr-scope">${escapeHtml(n.scope)}</td>` +
        `<td class="info-nr-formula">${escapeHtml(n.formula)}</td>` +
      `</tr>`,
    );
  }
  return `
    <details class="info-section">
      <summary class="info-section-header">
        <span class="info-section-title">Named ranges</span>
        ${invalidBadge}
        <span class="info-section-subtitle">${fmtNum(count)} total</span>
      </summary>
      <div class="info-section-body">
        <p class="info-nr-hint">
          For search, sort, and click-to-navigate, use the
          <em>List named ranges</em> action.
        </p>
        <table class="info-nr-table">
          <thead><tr><th>Name</th><th>Scope</th><th>Refers to</th></tr></thead>
          <tbody>${rows.join("")}</tbody>
        </table>
      </div>
    </details>
  `;
}

function fmtNum(n: number): string {
  return n.toLocaleString();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
